// src/pages/platform/PlatformDetailPage.tsx

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { usePlatform, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Alert,
  Badge,
  ErrorAlert,
} from '@react/components';
import type { PlatformStatus } from '@core/types/platform';
import type { PlatformUsage } from '@core/types/platform/PlatformUsage';

function mapStatusToVariant(
  status: string,
): 'success' | 'warning' | 'error' | 'primary' | 'secondary' {
  switch (status) {
    case 'PLATFORM_STATUS_ACTIVE':
      return 'success';
    case 'PLATFORM_STATUS_PENDING':
      return 'warning';
    case 'PLATFORM_STATUS_SUSPENDED':
      return 'error';
    case 'PLATFORM_STATUS_MAINTENANCE':
      return 'primary';
    case 'PLATFORM_STATUS_ARCHIVED':
    case 'PLATFORM_STATUS_DELETED':
      return 'secondary';
    default:
      return 'secondary';
  }
}

function formatStatusLabel(status: string): string {
  return status
    .replace('PLATFORM_STATUS_', '')
    .charAt(0)
    .toUpperCase()
    + status
        .replace('PLATFORM_STATUS_', '')
        .slice(1)
        .toLowerCase();
}

function formatDate(dateStr: string | undefined | null): string {
  if (!dateStr) return '--';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return String(dateStr);
  }
}

function formatBytes(bytes: number | undefined | null): string {
  if (bytes == null || bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const value = bytes / Math.pow(1024, i);
  return `${value.toFixed(i > 0 ? 1 : 0)} ${units[i]}`;
}

function formatNumber(value: number | undefined | null): string {
  if (value == null) return '0';
  return value.toLocaleString();
}

// Inline SVG icon components for stat cards
function TenantsIcon() {
  return (
    <svg className="h-6 w-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  );
}

function UsersIcon() {
  return (
    <svg className="h-6 w-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  );
}

function ProductsIcon() {
  return (
    <svg className="h-6 w-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
        d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
    </svg>
  );
}

function StorageIcon() {
  return (
    <svg className="h-6 w-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
        d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
    </svg>
  );
}

const QUICK_LINKS = [
  { labelKey: 'platform.nav.members', path: 'members', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z' },
  { labelKey: 'platform.nav.products', path: 'products', icon: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4' },
  { labelKey: 'platform.nav.settings', path: 'settings', icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z' },
  { labelKey: 'platform.nav.federation', path: 'federation', icon: 'M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
];

/**
 * Platform detail / dashboard page.
 * Shows platform info, usage stats, and quick navigation links.
 */
export const PlatformDetailPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const store = usePlatform();
  const { t } = useI18n();
  const item = store.currentItem as any;

  const [usage, setUsage] = useState<PlatformUsage | null>(null);
  const [usageLoading, setUsageLoading] = useState(false);

  useEffect(() => {
    if (id) {
      store.GetPlatform(id);

      // Load usage stats
      setUsageLoading(true);
      store
        .GetPlatformUsage(id)
        .then((res) => {
          setUsage(res.usage ?? (res as any));
        })
        .catch(() => {
          // Usage stats may not be available, that is ok
        })
        .finally(() => setUsageLoading(false));
    }
    return () => {
      store.clearCurrent();
    };
  }, [id]);

  const handleStatusChange = async (newStatus: PlatformStatus) => {
    if (!id) return;
    try {
      await store.SetPlatformStatus(id, { status: newStatus } as any);
      store.GetPlatform(id);
    } catch {
      // Error handled by store
    }
  };

  const isActive = item?.status === 'PLATFORM_STATUS_ACTIVE';
  const isSuspended = item?.status === 'PLATFORM_STATUS_SUSPENDED';

  if (store.isLoading || store.isLoadingItem) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  if (store.error) {
    return (
      <>
        <ErrorAlert error={store.error} />
        <Link to="/platforms" className="mt-2 inline-block text-sm underline text-primary">
          {t('common.backToList')}
        </Link>
      </>
    );
  }

  if (!item) {
    return (
      <Alert variant="warning" title={t('common.notFound')}>
        <Link to="/platforms" className="mt-2 inline-block text-sm underline text-primary">
          {t('common.backToList')}
        </Link>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="text-sm">
        <Link to="/platforms" className="text-muted-foreground hover:text-foreground">
          {t('platform.list')}
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <span className="text-foreground">{String(item.name ?? t('platform.details'))}</span>
      </nav>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="h-12 w-12 rounded-lg bg-muted flex items-center justify-center">
            <span className="text-lg font-semibold text-muted-foreground">
              {String(item.name ?? '').charAt(0).toUpperCase() || '?'}
            </span>
          </div>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-foreground">{String(item.name ?? '')}</h1>
              <Badge variant={mapStatusToVariant(item.status)} size="sm">
                {formatStatusLabel(item.status || '')}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {String(item.slug ?? '')}
              {item.created_at && (
                <> &middot; {t('platform.createdOn')} {formatDate(item.created_at)}</>
              )}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Link to={`/platforms/${id}/edit`}>
            <Button variant="secondary">{t('common.edit')}</Button>
          </Link>
          <Link to={`/platforms/${id}/settings`}>
            <Button variant="secondary">{t('platform.nav.settings')}</Button>
          </Link>
          {isActive ? (
            <Button
              variant="danger"
              onClick={() => handleStatusChange('PLATFORM_STATUS_SUSPENDED')}
            >
              {t('platform.action.suspend')}
            </Button>
          ) : isSuspended ? (
            <Button
              variant="primary"
              onClick={() => handleStatusChange('PLATFORM_STATUS_ACTIVE')}
            >
              {t('platform.action.activate')}
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={() => handleStatusChange('PLATFORM_STATUS_ACTIVE')}
            >
              {t('platform.action.activate')}
            </Button>
          )}
        </div>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {/* Stats cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 rounded-lg bg-primary/10 p-3">
                <TenantsIcon />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {usageLoading ? '--' : formatNumber(usage?.tenants_count)}
                </p>
                <p className="text-sm text-muted-foreground">{t('platform.stats.tenants')}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 rounded-lg bg-primary/10 p-3">
                <UsersIcon />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {usageLoading ? '--' : formatNumber(usage?.users_count)}
                </p>
                <p className="text-sm text-muted-foreground">{t('platform.stats.users')}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 rounded-lg bg-primary/10 p-3">
                <ProductsIcon />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {usageLoading ? '--' : formatNumber(usage?.products_count)}
                </p>
                <p className="text-sm text-muted-foreground">{t('platform.stats.products')}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 rounded-lg bg-primary/10 p-3">
                <StorageIcon />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {usageLoading ? '--' : formatBytes(usage?.storage_bytes_used)}
                </p>
                <p className="text-sm text-muted-foreground">{t('platform.stats.storage')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Platform info */}
      <Card>
        <CardHeader>
          <CardTitle>{t('platform.info')}</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">
                {t('platform.field.description')}
              </dt>
              <dd className="text-sm text-foreground col-span-2">
                {String(item.description ?? '--')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">
                {t('platform.field.slug')}
              </dt>
              <dd className="text-sm text-foreground col-span-2 font-mono">
                {String(item.slug ?? '--')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">
                {t('platform.field.owner')}
              </dt>
              <dd className="text-sm text-foreground col-span-2">
                {String(item.owner?.display_name || item.owner?.name || '--')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">
                {t('platform.field.createdAt')}
              </dt>
              <dd className="text-sm text-foreground col-span-2">
                {formatDate(item.created_at)}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">
                {t('platform.field.updatedAt')}
              </dt>
              <dd className="text-sm text-foreground col-span-2">
                {formatDate(item.updated_at)}
              </dd>
            </div>
            {item.suspended_at && (
              <div className="py-3 grid grid-cols-3 gap-4">
                <dt className="text-sm font-medium text-muted-foreground">
                  {t('platform.field.suspendedAt')}
                </dt>
                <dd className="text-sm text-destructive col-span-2">
                  {formatDate(item.suspended_at)}
                </dd>
              </div>
            )}
          </dl>
        </CardContent>
      </Card>

      {/* Quick links */}
      <Card>
        <CardHeader>
          <CardTitle>{t('platform.quickLinks')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {QUICK_LINKS.map((link) => (
              <Link
                key={link.path}
                to={`/platforms/${id}/${link.path}`}
                className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-muted transition-colors group"
              >
                <div className="flex-shrink-0 rounded-md bg-primary/10 p-2 group-hover:bg-primary/20 transition-colors">
                  <svg
                    className="h-5 w-5 text-primary"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d={link.icon}
                    />
                  </svg>
                </div>
                <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                  {t(link.labelKey)}
                </span>
                <svg
                  className="ml-auto h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </Link>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
});

export default PlatformDetailPage;
